import { observer } from 'mobx-react';
import { StepTitle } from '../common/stepTitle';
import { Button } from '@roc/ui';
import { Layout } from '@roc/feature-app-core';
import { Box, Grid, makeStyles, Paper, Typography } from '@material-ui/core';
import { OneToolCardBorder } from '../fixFlip/oneToolCard';
import { QuoteStep } from 'libs/feature-one-tool/src/quote/utils/constants';
import { CapitalProviderInfo } from '../common/capitalProviderInfo';
import { LoanSubType } from '@roc/feature-utils';
import { PropertyAddress } from '../fixFlip/propertyInformationFields';
import { HomeWorkOutlined, Edit } from '@material-ui/icons';
import { getCompletePropertyAddress } from '@roc/ui/utils';
import { ClosingDetailsCard } from '../common/closingDetailsCard'

const useStyles = makeStyles(theme => ({
  title: {
    fontSize: '34px',
  },
  paper: {},

  stepperWrapper: {
    flex: 1,
    padding: theme.spacing(3.5, 3.5),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1, 1),
    },
  },
}));

const PropertyCard = observer(
  ({ propertyFormStore, propertyIndex, quoteTypeStore, onSave, hasOtherEditingProperty, onEdit }) => {
    const property = propertyFormStore.getFormValues();
    const isEditing = propertyFormStore.form.meta.editMode;
    const completeAddress = getCompletePropertyAddress(property);

    const handleSave = async () => {
      propertyFormStore.validateAndCleanupProperty();
      if (propertyFormStore.form.meta.isValid) {
        propertyFormStore.setEditMode(false);
        if (onSave) {
          onSave();
        }
      }
    };

    const handleEdit = () => {
      if (onEdit) {
        onEdit();
      }
      propertyFormStore.setEditMode(true);
    };

    return (
      <OneToolCardBorder>
        <Box px={4} py={3}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" justifyContent="space-between" pb={isEditing ? 2 : 0}>
                <Box display="flex" alignItems="center">
                  <HomeWorkOutlined style={{ marginRight: 8, color: '#6D6D6D' }} />
                  <Typography variant="h6" style={{ color: '#6D6D6D' }}>
                    {property.propertyType || 'N/A'} - {completeAddress || 'Address not provided'}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={isEditing ? null : <Edit />}
                  onClick={isEditing ? handleSave : handleEdit}
                  disabled={!isEditing && hasOtherEditingProperty}
                  testId={`${isEditing ? 'save' : 'edit'}-property-${propertyIndex}`}
                >
                  {isEditing ? 'Save' : 'Edit'}
                </Button>
              </Box>
            </Grid>
            {isEditing && (
              <Grid item xs={12}>
                <PropertyAddress store={propertyFormStore} quoteTypeStore={quoteTypeStore} />
              </Grid>
            )}
          </Grid>
        </Box>
      </OneToolCardBorder>
    );
  }
);

export const PortfolioFinalDetailsStep = observer(({ store }) => {
  const classes = useStyles();
  const formStore = store.finalDetailsStore.finalDetailsFormStore;
  const isRentalPortfolio = store.loanSubtype === LoanSubType.RENTAL_PORTFOLIO;
  const propertyFormStores = isRentalPortfolio ? store.propertiesStore.propertyFormStores : [];

  const hasAnyEditingProperty = propertyFormStores.some(
    propertyStore => propertyStore.form.meta.editMode
  );

  const closeAllProperties = () => {
    propertyFormStores.forEach(propertyStore => {
      propertyStore.setEditMode(false);
    });
  };

  const handlePropertySave = (currentIndex: number) => {
    const nextIndex = currentIndex + 1;
    if (nextIndex < propertyFormStores.length) {
      const nextProperty = propertyFormStores[nextIndex];
      const nextPropertyHasAddress = nextProperty.form.fields.address.value;

      if (!nextPropertyHasAddress) {
        nextProperty.setEditMode(true);
      }
    }
  };

  return (
    <Layout maxWidth={'lg'}>
      <Box display={'flex'} alignItems={'center'} pb={2}>
        <Typography
          align="left"
          variant="h2"
          style={{
            color: '#5E5E5E',
            fontSize: '48px',
          }}
        >
          Final Details
        </Typography>
        <Box flex={1} />
        <CapitalProviderInfo capitalProvider={store.capitalProviderName} />
      </Box>
      <Paper className={classes.paper} elevation={0}>
        <Box display="flex">
          <Box className={classes.stepperWrapper}>
            <Grid container spacing={2}>
              {isRentalPortfolio && propertyFormStores.length > 0 && (
                <>
                  <Grid item xs={12}>
                    <StepTitle
                      title={'Property Information'}
                      subtitle={
                        'Please provide the address for each property in your portfolio.'
                      }
                    />
                  </Grid>
                  {propertyFormStores.map((propertyFormStore, index) => {
                    const isThisEditing = propertyFormStore.form.meta.editMode;
                    const hasOtherEditing = hasAnyEditingProperty && !isThisEditing;

                    return (
                      <Grid item xs={12} key={propertyFormStore.form.meta.key || index}>
                        <PropertyCard
                          propertyFormStore={propertyFormStore}
                          propertyIndex={index}
                          quoteTypeStore={store}
                          onSave={() => handlePropertySave(index)}
                          hasOtherEditingProperty={hasOtherEditing}
                          onEdit={closeAllProperties}
                        />
                      </Grid>
                    );
                  })}
                </>
              )}
              <ClosingDetailsCard formStore={formStore} />
              <Grid item xs={12}>
                <Box pt={3}>
                  <Grid
                    container
                    direction="row"
                    justifyContent="center"
                    spacing={2}
                  >
                    <Grid item xs={6} md={3}>
                      <Button
                        variant="outlined"
                        color="primary"
                        fullWidth
                        onClick={() => store.goToStep(QuoteStep.SUMMARY)}
                        testId="previous"
                      >
                        Back
                      </Button>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Button
                        testId="continue"
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={() => store.submitLoan()}
                      >
                        SAVE LOAN
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Paper>
    </Layout>
  );
});
